import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import {
  BiCardView,
  BiNarrowRows,
  kBiMaxItemHeight,
} from '@euler/model/bi/dashboard';
import { ReactNode, memo, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import {
  biRowsHeight,
  kBiGridGapPx,
  kBiGridRowPx,
} from '../../functions/biGridLayout';
import { NarrowResizeHandle } from './NarrowResizeHandle';

/** The rows a height snaps to, within the grid's bounds. */
function rowsAt(px: number): number {
  return Math.min(
    kBiMaxItemHeight,
    Math.max(1, Math.round((px + kBiGridGapPx) / kBiGridRowPx)),
  );
}

type BiRenderStacked = (
  card: BiCardView,
  height: number | undefined,
  grip?: ReactNode,
  footer?: ReactNode,
) => ReactNode;

/**
 * Phone: one card per row, top to bottom, each as tall as a phone resize
 * left it, else as tall as its content — a chart at its plot's height, a
 * table with every row, a KPI group as its tiles wrap — so no card scrolls
 * inside or sits half empty. Editors drag a card's bottom edge to resize it
 * here only.
 */
export const StackedGrid = memo(
  ({
    cardIds,
    cards,
    narrowRows,
    renderCard,
    onResize,
  }: {
    cardIds: string[];
    cards: Map<string, BiCardView>;
    narrowRows: BiNarrowRows | undefined;
    renderCard: BiRenderStacked;
    /** Given to editors; false when the save was refused. */
    onResize?: (cardId: string, rows: number) => Promise<boolean>;
  }) => {
    return (
      <View style={{ gap: kBiGridGapPx }}>
        {cardIds.map(id => {
          const card = cards.get(id);
          if (!card) return null;
          const stored = narrowRows?.[id];
          return (
            <StackedItem
              key={id}
              card={card}
              stored={stored}
              height={stored ? biRowsHeight(stored) : undefined}
              renderCard={renderCard}
              onResize={onResize}
            />
          );
        })}
      </View>
    );
  },
);

/**
 * One phone card. A drag draws only an outline of the new height (the card
 * keeps its size until the drop, so no chart redraws per frame); the drop
 * shows the new height at once and saves it.
 */
const StackedItem = memo(
  ({
    card,
    stored,
    height,
    renderCard,
    onResize,
  }: {
    card: BiCardView;
    /** The phone rows stored for it, if resized here before. */
    stored: number | undefined;
    /** Undefined: as tall as its content. */
    height: number | undefined;
    renderCard: BiRenderStacked;
    onResize?: (cardId: string, rows: number) => Promise<boolean>;
  }) => {
    const [outline, setOutline] = useState<number | null>(null);
    // A saved resize, shown until the dashboard comes back with it; each
    // save clears only its own, so a quicker second one is not undone.
    const [pending, setPending] = useState<{ rows: number; seq: number }>();
    const seq = useRef(0);
    // What the card measures, the start of a drag when it fits its content.
    const [measured, setMeasured] = useState(0);
    const shown = pending ? biRowsHeight(pending.rows) : height;
    const start = shown ?? measured;
    // What a drag does now, read by a grabber made once: the card is not
    // drawn again while it is dragged.
    const drag = useRef({
      move: (_dy: number) => {},
      end: (_dy: number) => {},
    });
    drag.current = {
      move: dy => setOutline(biRowsHeight(rowsAt(Math.max(1, start + dy)))),
      end: dy => {
        setOutline(null);
        // A nudge shorter than half a row is not a resize.
        if (!onResize || Math.abs(dy) < kBiGridRowPx / 2) return;
        const rows = rowsAt(start + dy);
        if (rows === (pending?.rows ?? stored)) return;
        const mine = ++seq.current;
        setPending({ rows, seq: mine });
        void onResize(card.id, rows).then(() =>
          setPending(current => (current?.seq === mine ? undefined : current)),
        );
      },
    };
    const resizable = !!onResize;
    const footer = useMemo(
      () =>
        resizable ? (
          <NarrowResizeHandle
            title={card.title}
            onMove={dy => drag.current.move(dy)}
            onEnd={dy => drag.current.end(dy)}
            onCancel={() => setOutline(null)}
          />
        ) : undefined,
      [resizable, card.title],
    );
    const body = useMemo(
      () => renderCard(card, shown, undefined, footer),
      [renderCard, card, shown, footer],
    );
    return (
      <View onLayout={event => setMeasured(event.nativeEvent.layout.height)}>
        {body}
        {outline !== null ? (
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              height: outline,
              borderRadius: 14,
              borderWidth: 2,
              borderStyle: 'dashed',
              borderColor: aiColors.primary,
              backgroundColor: tint(aiColors.primary, 0.06),
            }}
          />
        ) : null}
      </View>
    );
  },
);
